import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Dialog, makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import ReactGA from 'react-ga4';

import Title from "../../../uiKit/Title/Title";
import CustomButton from "../../../uiKit/Button/CustomButton";
import { MailMan } from '../../../uiKit/assets/svg/mailMan'
import CustomInput from "../../../uiKit/Input/CustomInput";
import CustomCheckbox from "../../../uiKit/CustomCheckbox/customCheckbox";
import { Donation } from "../../../uiKit/assets/svg/icons";
import { gray010 } from "../../../uiKit/assets/colors/colors";
import { onlyNumbers, validateEmail, validateName } from "../../../helpers/validation";
import { DONATE_ALL_INFO, DONATION_FEE } from "../../../helpers/constants";
import { setDonate } from "../../../redux/state/commonSlice";
import Checkout from "../../Checkout/checkout";
import CheckoutDonateAll from "../../Checkout/donateAllCheckout";

import { changeIsAnonymous, donateAllPaymentIntent } from "../../../redux/state/paymentSlice";
import { setAllThankOpen, setDonationBody, setStripeOpenDonateAll } from "../../../redux/state/donationSlice";
import DonateAllThankYou from "../../OurBalance/thankYouAll";

const useStyles = makeStyles(() => ({
    paper: {
        '&.MuiPaper-rounded': {
            borderRadius: '12px'
        }
    }
}))

const PredefinedOptions = [100, 54, 36, 18];

const DonateButton = () => {

    const dispatch = useDispatch();
    const dialogClasses = useStyles();

    const [coverFees, setCoverFees] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState('');

    const [lastName, setLastNameValue] = useState("");
    const [lastNameError, setLastNameError] = useState('');

    const [email, setEmailValue] = useState("");
    const [emailError, setEmailError] = useState('');

    const [amount, setAmount] = useState(null);
    const [amountError, setAmountError] = useState("");

    const open = useSelector(state => state.common.donateModal, shallowEqual);
    const userDetails = useSelector(state => state.account.userDetails, shallowEqual);
    const isAnonymous = useSelector(state => state.payment.isAnonymous, shallowEqual);
    const openStripe = useSelector(state => state.donation.stripeOpenDonateAll, shallowEqual);
    const allThankOpen = useSelector(state => state.donation.allThankOpen, shallowEqual);

    const closeHandler = () => dispatch(setDonate(false));
    const closeStripeHandler = () => dispatch(setStripeOpenDonateAll(false));
    const openDonationHandler = () => dispatch(setDonate(true));
    const closeAllThankHandler = () => dispatch(setAllThankOpen(false));

    const openStripeHandler = () => {
        ReactGA.event('donate_all_start')
        dispatch(setStripeOpenDonateAll(true))
        closeHandler();
    }

    const anonymousHandler = (event) => {
        const value = event.target.checked;
        dispatch(changeIsAnonymous(value));
    }

    const donateHandler = () => {
        const body = {
            amount,
            isAnonymous,
            userInfo: {
                email,
                firstName,
                lastName,
            },
            coverFees,
        }
        dispatch(setDonationBody(body));

        setIsLoading(true);
        dispatch(donateAllPaymentIntent({ body }))
            .then(unwrapResult)
            .then(() => openStripeHandler())
            .catch(({ title }) => {
                toast.info(`Payment failed: ${title}`)
            })
            .finally(() => setIsLoading(false));
    }

    const donationAmountHandler = (e) => {
        const value = e.target.value;
        if (onlyNumbers(value)) {
            if (value === '0') {
                setAmountError('Insufficient amount');
                setAmount(null);
            } else {
                setAmountError('');
                setAmount(parseInt(value));
            }
        } else {
            setAmountError('Required');
            setAmount(null);
        }
    }

    const coverFeeConfirmHandler = () => {
        setCoverFees(prev => !prev)
    }

    const onFirstNameChange = useCallback((e) => {
        const value = e.target.value;
        setFirstName(value);
        if (!value) {
            setFirstNameError('Required');
            return;
        }

        if (!validateName(value)) {
            setFirstNameError('Invalid first name');
        } else {
            setFirstNameError('');
        }
    }, [])

    const onLastNameChange = useCallback((e) => {
        const value = e.target.value;
        setLastNameValue(value);
        if (!value) {
            setLastNameError('Required');
            return;
        }

        if (!validateName(value)) {
            setLastNameError('Invalid last name')
        } else {
            setLastNameError('')
        }
    }, [])

    const onEmailChange = useCallback((e) => {
        const value = e.target.value;
        setEmailValue(value);

        if (!value) {
            setEmailError('Email required');
        }

        if (!validateEmail(value)) {
            setEmailError('Invalid email address');
        } else {
            setEmailError('');
        }

    }, [])

    const predefinedOptionsHandler = (amount) => {
        setAmount(prev => prev + amount)
    }

    useEffect(() => {
        if (!userDetails.id) return;
        setEmailValue(userDetails.email);
    }, [userDetails])



    return (
        <>
            <CustomButton
                title={"Donate to All"}
                onClick={openDonationHandler}
                Icon={() => <Donation color={gray010} />}
            />
            <Dialog
                open={open}
                onClose={closeHandler}
                disableScrollLock={true}
                classes={dialogClasses}
            >
                <div className='feedback page' >
                    <div className='feedbackContainer'>
                        <div className='feedbackImageContainer'>
                            <MailMan />
                        </div>
                        <div className='description'>
                            <Title
                                text={"Donate All"}
                                fontWeight={600}
                                lineHeight={40}
                                fontSize={32}
                                style={{ marginBottom: '16px' }}
                            />
                            <Title
                                text={DONATE_ALL_INFO}
                                lineHeight={20}
                                fontSize={14}
                                style={{ marginBottom: '16px' }}
                                fontWeight={400}
                            />
                            {
                                !userDetails?.id && <>
                                    <div style={{ display: "flex", gap: 12 }}>
                                        <CustomInput
                                            label='First Name'
                                            type='text'
                                            onChange={onFirstNameChange}
                                            value={firstName}
                                            errorMessage={firstNameError}
                                            error={!!firstNameError}
                                            setErrorMessage={setFirstNameError}
                                            maxLength={30}
                                        />
                                        <CustomInput
                                            label='Last Name'
                                            type='text'
                                            onChange={onLastNameChange}
                                            value={lastName}
                                            error={!!lastNameError}
                                            errorMessage={lastNameError}
                                            setErrorMessage={setLastNameError}
                                            maxLength={30}
                                        />
                                    </div>
                                    <CustomInput
                                        label='Email'
                                        type='email'
                                        onChange={onEmailChange}
                                        value={email}
                                        error={!!emailError}
                                        errorMessage={emailError}
                                    />
                                </>
                            }
                            <CustomInput
                                label={"Donation Amount"}
                                onChange={donationAmountHandler}
                                inputMode={"numeric"}
                                value={amount}
                                error={!!amountError}
                                errorMessage={amountError}
                                baseStyles={{ marginBottom: !amountError ? '8px' : '28px' }}
                            />
                            <div className="definedOptions">
                                {
                                    PredefinedOptions.map(x => <div >
                                        <Title text={`$ ${x}`} fontSize={12} onClick={() => predefinedOptionsHandler(x)} />
                                    </div>)
                                }
                            </div>
                            <CustomCheckbox
                                checked={isAnonymous}
                                onChange={anonymousHandler}
                                label='Donate Anonymously'
                            />
                            <CustomCheckbox
                                label={DONATION_FEE}
                                checked={coverFees}
                                onChange={coverFeeConfirmHandler}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: '24px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CustomButton
                            onClick={donateHandler}
                            title={"Confirm"}
                            loading={isLoading}
                            disabled={
                                !!emailError ||
                                !!amountError ||
                                !amount ||
                                !email ||
                                !!firstNameError ||
                                !!lastNameError
                            }
                        />
                        <CustomButton
                            onClick={closeHandler}
                            title='Decline'
                            isWhite
                            style={{ marginTop: '16px' }}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openStripe}
                onClose={closeStripeHandler}
                disableScrollLock={true}
                classes={dialogClasses}
            >
                <Checkout Component={CheckoutDonateAll} type={2} />
            </Dialog>
            <Dialog
                open={allThankOpen}
                onClose={closeAllThankHandler}
                disableScrollLock={true}
                classes={dialogClasses}
            >
                <DonateAllThankYou />
            </Dialog>
        </>
    )
}

export default DonateButton;